import React, { useMemo } from 'react';
import { Animated, View } from 'react-native';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { SCREEN_PADDING_SMALL, shadow } from '@/styles';
import { SvgProps } from 'react-native-svg';

const BUTTON_SIZE = 64;
const SCROLL_DISTANCE = 200;

export type LocationBottomAction =
  | {
      accessibilityLabel?: string;
      Icon: React.FC<SvgProps>;
      iconProps?: SvgProps;
      onPress: () => void;
    }
  | {
      component: React.ReactElement;
    };

interface Props {
  bottomActions: LocationBottomAction[];
  maxScrollDistance: number;
  scrollY: Animated.Value;
}

export const LocationBottomActions: React.FC<Props> = ({
  bottomActions,
  maxScrollDistance,
  scrollY,
}) => {
  const [styles] = useStyles();

  const [bottom, opacity] = useMemo(() => {
    const finalScrollDistance = maxScrollDistance
      ? Math.min(maxScrollDistance, SCROLL_DISTANCE)
      : SCROLL_DISTANCE;

    const diffClamp = Animated.diffClamp(scrollY, 0, finalScrollDistance);

    const bottom = diffClamp.interpolate({
      extrapolate: 'clamp',
      inputRange: [0, finalScrollDistance],
      outputRange: [0, -BUTTON_SIZE],
    });
    const opacity = diffClamp.interpolate({
      extrapolate: 'clamp',
      inputRange: [0, finalScrollDistance],
      outputRange: [1, 0],
    });

    return [bottom, opacity];
  }, [maxScrollDistance, scrollY]);

  const hasMultipleActions = bottomActions.length > 1;

  return (
    <Animated.View
      style={[
        styles.base,
        hasMultipleActions ? null : styles.rightAligned,
        { bottom, opacity },
      ]}
    >
      <View
        style={[
          styles.buttonGroup,
          hasMultipleActions ? styles.buttonGroupMultiple : null,
        ]}
      >
        {bottomActions.map((action, index) => {
          if ('component' in action) {
            return (
              <View key={index} style={styles.button}>
                {action.component}
              </View>
            );
          }

          return (
            <Button
              accessibilityLabel={action.accessibilityLabel}
              Icon={action.Icon}
              iconProps={action.iconProps}
              key={index}
              onPress={action.onPress}
              rounded={false}
              style={styles.button}
              // Handled within onPress
              trackingData={null}
            />
          );
        })}
      </View>
    </Animated.View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    alignItems: 'center',
    alignSelf: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: 48,
    paddingHorizontal: SCREEN_PADDING_SMALL,
    position: 'absolute',
  },
  button: {
    backgroundColor: theme.colors.transparent,
    borderWidth: 0,
    height: BUTTON_SIZE,
    width: BUTTON_SIZE,
  },
  buttonGroup: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderRadius: BUTTON_SIZE,
    flexDirection: 'row',
    ...shadow({
      elevation: 2,
      opacity: 0.15,
      radius: 4,
      x: 0,
      y: 4,
    }),
  },
  buttonGroupMultiple: {
    paddingHorizontal: 8,
  },
  rightAligned: {
    alignSelf: 'flex-end',
  },
}));
